import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { TransactionalSolidButton, SecondaryTextButton } from '@bibliocommons/base-buttons';

import TextArea from 'app/components/shared/TextArea';
import { ModalHeader, ModalContent, ModalFooter } from 'app/components/shared/Modal';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';
import { FEEDBACK_COMMENT_MAX_LENGTH } from 'app/constants/PreviewConstants';
import getBrowserInformation from 'app/helpers/getBrowserInformation';
import composeGlobalSuccessMessaging from 'app/helpers/messaging/composeGlobalSuccessMessaging';

import './ShareFeedbackModal.scss';

export const FEEDBACK_MODAL_KEY = 'comment-summary-feedback-modal';
// The AI-prefix convention: category is the canonical signal for AI-feature feedback,
// and it keys the submission transaction state (isLoading).
export const FEEDBACK_CATEGORY = 'AI_comment_summaries';

export default function ShareFeedbackModal({ commentSummary, isSubmitting, modalActions, previewActions }) {
  const [comment, setComment] = useState('');

  const closeAndDiscard = () => {
    setComment('');
    modalActions.closeModal(FEEDBACK_MODAL_KEY);
  };

  const handleSubmit = event => {
    if (event) {
      event.preventDefault();
    }
    if (!comment || isSubmitting) return;

    previewActions.submitFeedback({
      featureName: FEEDBACK_CATEGORY,
      feedback: {
        comment,
        ...getBrowserInformation(),
        category: FEEDBACK_CATEGORY,
        optIn: true,
        // Feedback provenance (PDS-554 contract): what the user actually saw.
        // The gateway drops `context` until its PDS-554 follow-up lands — by design.
        context: {
          comment_summary_text: commentSummary.get('summary'),
          comment_summary_prompt_version: commentSummary.get('promptVersion'),
          comment_summary_editions_id: commentSummary.get('editionsId')
        }
      },
      apiSuccess: () => ({
        // The existing toast + string, per the product spec ("Thank you for your feedback!")
        messaging: composeGlobalSuccessMessaging('opt_in_feedback_thanks', { useFormattedMessage: true })
      }),
      postSuccess: closeAndDiscard
    });
  };

  return (
    <ModalContainer modalKey={FEEDBACK_MODAL_KEY} dataKey={FEEDBACK_MODAL_KEY} handleClose={closeAndDiscard}>
      <form className="cp-comment-summary-feedback-form" onSubmit={handleSubmit}>
        <ModalHeader title={<FormattedMessage id="share_feedback" />} />
        <ModalContent dataKey="comment-summary-feedback-body">
          <p className="feedback-body">
            <FormattedMessage id="comment_summary_feedback_body" />
          </p>
          <TextArea
            className="feedback-comment"
            dataKey="comment-summary-feedback-input"
            id="comment-summary-feedback-input"
            label={<FormattedMessage id="feedback_label" />}
            maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
            minHeight="9rem"
            handleChange={setComment}
            required
            value={comment}
          />
        </ModalContent>
        <ModalFooter
          renderPrimaryAction={() => (
            <TransactionalSolidButton
              dataKey="comment-summary-feedback-submit"
              disabled={!comment}
              handleClick={handleSubmit}
              isLoading={isSubmitting}
            >
              <FormattedMessage id="button_submit" />
            </TransactionalSolidButton>
          )}
          renderSecondaryAction={() => (
            <SecondaryTextButton
              className="modal-footer__secondary-button"
              dataKey="comment-summary-feedback-cancel"
              handleClick={closeAndDiscard}
              type="button"
            >
              <FormattedMessage id="button_cancel" />
            </SecondaryTextButton>
          )}
        />
      </form>
    </ModalContainer>
  );
}

ShareFeedbackModal.propTypes = {
  commentSummary: ImmutablePropTypes.map.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  previewActions: PropTypes.objectOf(PropTypes.func).isRequired
};
